import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Typography,
} from "@mui/material";
import { Add, Android, ExpandMore } from "@mui/icons-material";
import JsonLd from "../components/JsonLd";
import FeatureShowcase, { Reveal } from "../components/FeatureShowcase";
import Hero from "../components/sections/Hero";
import { useSmoothScroll } from "../components/motion/SmoothScroll";
import StickyPlayCta from "../components/StickyPlayCta";
import { PLAY_STORE_URL } from "../config/store";
import { trackEvent } from "../utils/analytics";
import BlogPostCard from "../components/blog/BlogPostCard";
import { getFeaturedPosts } from "../content/blog";
import { mobileApplicationJsonLd } from "../utils/schema";

const SCREENSHOTS = {
  home: "/screenshots/home.png",
  add: "/screenshots/add-transaction.png",
  transactions: "/screenshots/transactions.png",
  reports: "/screenshots/reports.png",
  permissions: "/screenshots/permissions.png",
};

const FAQ_ITEMS = [
  {
    q: "Is Treasure free?",
    a: "Treasure is free to download on Google Play. You can start tracking income and expenses with the core money-management features without a paid subscription.",
  },
  {
    q: "Is Treasure available on Android?",
    a: "Yes. Treasure is available for Android on Google Play.",
  },
  {
    q: "Who is Treasure for?",
    a: "Treasure is for people who want a simple way to manage money—especially students with limited pocket money and young professionals starting their salary journey. Auction committee management is available as an additional capability.",
  },
  {
    q: "Can I track income and expenses?",
    a: "Yes. You can record income and expense transactions with date, amount, remark, and category.",
  },
  {
    q: "Can I view reports and insights?",
    a: "Yes. Treasure includes reports and category breakdowns so you can see income, expenses, and where your money goes over time.",
  },
  {
    q: "Is my financial data private?",
    a: "We do not sell your personal or financial information for marketing. Data is stored using secure infrastructure (including Google Firebase). Optional features like expense reminders and auto-fill from bank alerts stay under your control and can be turned off anytime. See our Privacy Policy for details.",
  },
  {
    q: "What is the Auction Committee feature?",
    a: "Auction committees are an additional capability in Treasure. You can create a committee, add members with contact details, and members added to a committee can view it in the app.",
  },
];

const LANDING_JSON_LD = () => ({
  "@context": "https://schema.org",
  "@graph": [
    mobileApplicationJsonLd(),
    {
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ],
});

const BLUE = "#5E35B1";
const PANEL = "#EDE7F6";
const DOTS = "radial-gradient(rgba(0,0,0,0.09) 1px, transparent 1px)";

const titleSx = {
  fontWeight: 500,
  fontSize: { xs: "1.75rem", md: "2.25rem" },
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#000",
};
const leadSx = { color: "rgba(0,0,0,0.6)", lineHeight: 1.6, fontSize: { xs: "0.95rem", md: "1.05rem" } };
const labelSx = { color: BLUE, fontWeight: 500, fontSize: "0.75rem", letterSpacing: "0.06em", textTransform: "uppercase" };

const blueBtnSx = {
  bgcolor: BLUE,
  color: "#fff",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  px: 2.5,
  py: 1,
  boxShadow: "none",
  "&:hover": { bgcolor: "#4527A0", boxShadow: "none" },
};

const PlayCta = ({ label = "Get Treasure on Google Play", eventName, sx = {} }) => (
  <Button
    component="a"
    href={PLAY_STORE_URL}
    target="_blank"
    rel="noopener noreferrer"
    variant="contained"
    startIcon={<Android />}
    onClick={() => eventName && trackEvent(eventName)}
    sx={{ ...blueBtnSx, ...sx }}
  >
    {label}
  </Button>
);

const Panel = ({ children, sx = {}, ...rest }) => (
  <Box sx={{ bgcolor: PANEL, borderRadius: "24px", p: { xs: 2.5, md: 5 }, ...sx }} {...rest}>
    {children}
  </Box>
);

const Section = ({ id, children, sx = {}, ...rest }) => (
  <Box id={id} component="section" sx={{ py: { xs: 6, md: 9 }, px: 2, scrollMarginTop: 80, ...sx }} {...rest}>
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

const Heading = ({ id, label, children, lead }) => (
  <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 }, maxWidth: 640, mx: "auto" }}>
    {label && <Typography sx={{ ...labelSx, mb: 2 }}>{label}</Typography>}
    <Typography id={id} component="h2" sx={titleSx}>
      {children}
    </Typography>
    {lead && <Typography sx={{ ...leadSx, mt: 2 }}>{lead}</Typography>}
  </Box>
);

const Blue = ({ children }) => <Box component="span" sx={{ color: BLUE }}>{children}</Box>;

const SHOWCASE_A = [
  { title: "Added in seconds.", body: "Enter the amount, pick a category, add a remark and you are done. Every rupee is recorded the moment it moves.", shot: SCREENSHOTS.add, alt: "Treasure add transaction screen" },
  { title: "Every entry, in one list.", body: "Scroll your income and expenses by date with categories and remarks, so daily spending is never buried in your head.", shot: SCREENSHOTS.transactions, alt: "Treasure transactions list" },
  { title: "Your month at a glance.", body: "Income, expenses and remaining balance together, so you always know where you stand before payday.", shot: SCREENSHOTS.home, alt: "Treasure home dashboard" },
];

const SHOWCASE_B = [
  { title: "See where it goes.", body: "Category charts show which spends dominate the month, so you can adjust with confidence.", shot: SCREENSHOTS.reports, alt: "Treasure reports screen" },
  { title: "Yearly and monthly views.", body: "Switch between year and month to spot trends, totals and your net balance.", shot: SCREENSHOTS.reports, alt: "Treasure yearly and monthly reports" },
  { title: "You stay in control.", body: "Expense reminders and auto-fill from bank alerts are optional and can be turned off anytime in Manage permissions.", shot: SCREENSHOTS.permissions, alt: "Treasure manage permissions screen" },
];

const TOOLS = [
  ["Transactions", "Record income and expenses with date, amount, remark and category."],
  ["Categories", "Group your spending so patterns become obvious."],
  ["Reports", "Summaries, trends and statistics for the year or month."],
  ["Reminders & auto-fill", "Optional expense reminders and auto-fill from bank alerts."],
  ["Auction committees", "Create committees, add members and give them transparent access in the app."],
];

const ToolRows = () => {
  const [open, setOpen] = useState(0);
  return (
    <Box>
      {TOOLS.map(([t, b], i) => {
        const on = open === i;
        return (
          <Box key={t} sx={{ borderTop: "1px solid rgba(0,0,0,0.1)", "&:last-of-type": { borderBottom: "1px solid rgba(0,0,0,0.1)" } }}>
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={on}
              onClick={() => setOpen(on ? -1 : i)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen(on ? -1 : i))}
              sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 3, cursor: "pointer", outline: "none", "&:hover .wx-t": { color: BLUE }, "&:focus-visible": { outline: `2px solid ${BLUE}` } }}
            >
              <Typography className="wx-t" sx={{ fontSize: { xs: "1.25rem", md: "1.6rem" }, fontWeight: 500, color: on ? BLUE : "#000", transition: "color .3s" }}>{t}</Typography>
              <Add sx={{ color: BLUE, transition: "transform .4s", transform: on ? "rotate(135deg)" : "none" }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateRows: on ? "1fr" : "0fr", transition: "grid-template-rows .5s cubic-bezier(0.22, 1, 0.36, 1)" }}>
              <Box sx={{ overflow: "hidden" }}>
                <Typography sx={{ ...leadSx, pb: 3, maxWidth: 560, opacity: on ? 1 : 0, transition: "opacity .4s" }}>{b}</Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const LandingPage = () => {
  useSmoothScroll();
  const jsonLd = useMemo(LANDING_JSON_LD, []);
  const [openFaq, setOpenFaq] = useState(false);

  useEffect(() => {
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const t = window.setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 80);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <Box sx={{ bgcolor: "#fff", fontFamily: '"Roboto", "Poppins", sans-serif', "& *": { fontFamily: "inherit" }, pb: { xs: 10, md: 0 } }}>
      <JsonLd data={jsonLd} id="jsonld-landing" />
      <StickyPlayCta hideWhenSelector="#final-cta" />

      <Hero />

      {/* Feature showcases */}
      <Section id="how-it-works">
        <Heading label="Money made simple" id="how-heading" lead="Record it, understand it and plan what is left, all on the phone you already carry.">
          <Box component="span" sx={{ color: "rgba(0,0,0,0.5)" }}>The worst part of the month is now the </Box>
          <Blue>easiest.</Blue>
        </Heading>
        <FeatureShowcase label="Track" title="Your money, recorded in a few taps." items={SHOWCASE_A} />
        <Box sx={{ height: { xs: 24, md: 40 } }} />
        <FeatureShowcase label="Understand" title="Reports that tell you the story." items={SHOWCASE_B} reverse />
      </Section>

      {/* Students + professionals */}
      <Section id="students" sx={{ pt: 0 }}>
        <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
          <Panel id="professionals" sx={{ scrollMarginTop: 80, display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 4 }}>
            <Box>
              <Typography sx={{ ...labelSx, mb: 2 }}>For students</Typography>
              <Typography component="h2" sx={{ ...titleSx, fontSize: { xs: "1.5rem", md: "1.9rem" } }}>Make your monthly pocket money last longer.</Typography>
              <Typography sx={{ ...leadSx, mt: 1.5 }}>Track what you receive, see where you spend it and know what you have left.</Typography>
            </Box>
            <Box><PlayCta label="Start tracking" eventName="student_cta_click" sx={{ bgcolor: "#000", "&:hover": { bgcolor: "#222" } }} /></Box>
          </Panel>
          <Panel sx={{ bgcolor: "#fff", border: "1px solid rgba(0,0,0,0.08)", backgroundImage: DOTS, backgroundSize: "14px 14px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 4 }}>
            <Box>
              <Typography sx={{ ...labelSx, mb: 2 }}>For professionals</Typography>
              <Typography component="h2" sx={{ ...titleSx, fontSize: { xs: "1.5rem", md: "1.9rem" } }}>Your first salary needs a plan.</Typography>
              <Typography sx={{ ...leadSx, mt: 1.5 }}>From groceries to recurring bills and impulse buys, understand your month before it gets away from you.</Typography>
            </Box>
            <Box><PlayCta eventName="professional_cta_click" /></Box>
          </Panel>
        </Box>
      </Section>

      {/* Tools */}
      <Section id="features" sx={{ pt: 0 }}>
        <Reveal>
          <Heading label="Everything in one app" id="features-heading">Simple tools. <Blue>Clearer decisions.</Blue></Heading>
          <Box sx={{ maxWidth: 820, mx: "auto" }}><ToolRows /></Box>
        </Reveal>
      </Section>

      {/* Privacy */}
      <Section id="privacy" sx={{ pt: 0 }}>
        <Panel sx={{ textAlign: "center" }}>
          <Typography sx={{ ...labelSx, mb: 2 }}>Secure &amp; private</Typography>
          <Typography component="h2" sx={{ ...titleSx, maxWidth: 640, mx: "auto" }}>Your data is yours.</Typography>
          <Typography sx={{ ...leadSx, mt: 2, maxWidth: 640, mx: "auto" }}>
            We do not sell your personal or financial information for marketing. Data is stored on secure infrastructure,
            including Google Firebase, and optional features can be turned off anytime.
          </Typography>
          <Button component={RouterLink} to="/privacy-policy" sx={{ ...blueBtnSx, mt: 3 }}>Read Privacy Policy</Button>
        </Panel>
      </Section>

      {/* Guides */}
      <Section sx={{ pt: 0 }}>
        <Heading label="Guides" id="guides-heading" lead="Short reads on student budgets, first salary and tracking expenses on Android.">Guides for your money</Heading>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5 }}>
          {getFeaturedPosts(4).map((post) => (
            <BlogPostCard key={post.slug} post={post} />
          ))}
        </Box>
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Button component={RouterLink} to="/blog" sx={{ ...blueBtnSx }}>Read the Treasure blog</Button>
        </Box>
      </Section>

      {/* FAQ */}
      <Section sx={{ pt: 0 }}>
        <Box sx={{ maxWidth: 760, mx: "auto" }}>
          <Heading label="FAQ" id="faq-heading">Frequently asked questions</Heading>
          {FAQ_ITEMS.map((item, index) => (
            <Accordion
              key={item.q}
              disableGutters
              elevation={0}
              expanded={openFaq === index}
              onChange={(_, expanded) => setOpenFaq(expanded ? index : false)}
              sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)", "&:before": { display: "none" }, bgcolor: "transparent" }}
            >
              <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`faq-${index}-content`} id={`faq-${index}-header`}>
                <Typography sx={{ fontWeight: 500 }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ ...leadSx, fontSize: "0.95rem" }}>{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Section>

      {/* Final CTA */}
      <Section id="final-cta" sx={{ pt: 0 }}>
        <Panel sx={{ textAlign: "center", py: { xs: 6, md: 9 } }}>
          <Typography id="final-cta-heading" component="h2" sx={titleSx}>Ready to take control of your money?</Typography>
          <Typography sx={{ ...leadSx, mt: 2, mb: 3.5 }}>Start tracking your income and expenses with Treasure.</Typography>
          <PlayCta eventName="final_google_play_click" />
        </Panel>
      </Section>
    </Box>
  );
};

export default LandingPage;
